// src/services/aeo/lineWords.ts
//
// How a plan line and its document read on screen, whatever the plan data
// carries. Bee Choo Origin's plan (27 Oct 2026) named a line "Articles
// published (2–3)", the count range from the quotation, and its document
// "Articles 1 and 2", so a row read "Read the Articles 1 and 2, then share it".

/** A count in brackets at the end of a line's name: "(2–3)", "(1)", "(2 - 3)". */
const TRAILING_COUNT = /\s*\(\s*\d+(?:\s*[–—-]\s*\d+)?\s*\)\s*$/;

/** A plan line's name without the count the quotation put after it. */
export function lineLabel(label: string): string {
    const clean = label.replace(TRAILING_COUNT, "");
    return clean || label;
}

/**
 * A document's name inside a step. "the Answer Plan" and "it", but a
 * numbered set ("Articles 1 and 2") takes no "the", and more than one is "them".
 */
function docWords(name: string): { name: string; it: string } {
    const numbered = /\d/.test(name);
    const many = /\band\b|&/.test(name);
    return { name: numbered ? name : `the ${name}`, it: many ? "them" : "it" };
}

/** "Read the Answer Plan, then share it"; "Read Articles 1 and 2, then share them". */
export function readThenShare(docName: string): string {
    const w = docWords(docName);
    return `Read ${w.name}, then share ${w.it}`;
}

/** "Create the Answer Plan", with ", then share it" when asked. */
export function createDoc(docName: string, thenShare = false): string {
    const w = docWords(docName);
    return thenShare ? `Create ${w.name}, then share ${w.it}` : `Create ${w.name}`;
}

/** A step the server wrote with the old wording, read again: "Read the Articles 1 and 2, then share it". */
export function stepText(text: string): string {
    const read = /^Read the (.+), then share it$/.exec(text);
    if (read) return readThenShare(read[1]);
    const make = /^Create the (.+?)(, then share it)?$/.exec(text);
    if (make) return createDoc(make[1], Boolean(make[2]));
    return text;
}

/** A Delivery line as it reads on screen: its name without the count, its step in the words above. */
export function readableDeliverable<D extends { label: string; next?: { text: string } | null }>(d: D): D {
    const label = lineLabel(d.label);
    const text = d.next ? stepText(d.next.text) : null;
    if (label === d.label && (!d.next || text === d.next.text)) return d;
    return { ...d, label, ...(d.next && text !== null ? { next: { ...d.next, text } } : {}) };
}
